import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { SEED_INCIDENTS, SIMULATOR_SCENARIOS, buildTimeline } from "./demoData";

const DemoContext = createContext(null);
const STORAGE_KEY = "norse3_demo_state_v1";

let counter = 5000;
const nextRef = () => `INC-${++counter}`;

function loadInitial() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.incidents)) return parsed.incidents;
    }
  } catch (e) {
    /* ignore */
  }
  return SEED_INCIDENTS.map((i) => ({ ...i }));
}

export function DemoProvider({ children }) {
  const [incidents, setIncidents] = useState(loadInitial);
  const [currentIncidentId, setCurrentIncidentId] = useState(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ incidents }));
    } catch (e) {
      /* ignore */
    }
  }, [incidents]);

  const runScenario = useCallback((scenarioId) => {
    const s = SIMULATOR_SCENARIOS.find((x) => x.id === scenarioId);
    if (!s) return null;
    const id = nextRef();
    const incident = {
      id,
      source: "simulator",
      interactionType: s.interactionType,
      channel: s.channel,
      createdAt: new Date().toISOString(),
      input: s.input,
      risk: s.risk,
      riskFamily: s.riskFamily,
      severity: s.severity,
      policy: s.policy,
      policyVersion: s.policyVersion,
      detection: s.detection,
      action: s.action,
      outcome: s.outcome,
      escalationRoute: s.escalationRoute,
      status: s.action === "Escalate" || s.action === "Hold" ? "Awaiting review" : "Open",
      reviewerDecision: null,
      reviewerNotes: "",
      confidence: s.confidence,
    };
    setIncidents((prev) => [incident, ...prev]);
    setCurrentIncidentId(id);
    return incident;
  }, []);

  const addFromCall = useCallback((call, flag) => {
    const id = nextRef();
    const incident = {
      id,
      source: "logberg",
      interactionType: "Recorded call",
      channel: "Recorded regulated call",
      createdAt: new Date().toISOString(),
      input: `${call.id} — ${flag ? flag.why : call.summary}`,
      risk: flag ? flag.category : call.categories.join(", "),
      riskFamily: "Conduct & advice",
      severity: call.risk === "High" ? "High" : call.risk === "Medium" ? "Medium" : "Low",
      policy: flag ? flag.policy : "Call quality assurance",
      policyVersion: "v1.0",
      detection: flag ? flag.why : call.summary,
      action: flag ? flag.suggested : call.suggestedAction,
      outcome: "Placed in the reviewer queue with timestamped evidence.",
      escalationRoute: "Compliance reviewer",
      status: "Awaiting review",
      reviewerDecision: null,
      reviewerNotes: "",
      confidence: flag ? flag.confidence : call.confidence,
    };
    setIncidents((prev) => [incident, ...prev]);
    setCurrentIncidentId(id);
    return incident;
  }, []);

  const reviewIncident = useCallback((id, decision, note) => {
    setIncidents((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        let status = i.status;
        if (decision === "Confirm issue") status = "Resolved";
        else if (decision === "False positive") status = "Resolved";
        else if (decision === "Escalate") status = "Escalated";
        else if (decision === "Mark reviewed") status = "Resolved";
        return {
          ...i,
          reviewerDecision: decision,
          reviewerNotes: note != null ? note : i.reviewerNotes,
          status,
        };
      })
    );
  }, []);

  const markResolved = useCallback((id) => {
    setIncidents((prev) => prev.map((i) => (i.id === id ? { ...i, status: "Resolved" } : i)));
  }, []);

  const resetDemo = useCallback(() => {
    setIncidents(SEED_INCIDENTS.map((i) => ({ ...i })));
    setCurrentIncidentId(null);
  }, []);

  const getIncident = useCallback((id) => incidents.find((i) => i.id === id) || null, [incidents]);

  const stats = useMemo(() => {
    const by = (fn) => incidents.reduce((acc, i) => { const k = fn(i); acc[k] = (acc[k] || 0) + 1; return acc; }, {});
    return {
      total: incidents.length,
      open: incidents.filter((i) => i.status === "Open").length,
      awaiting: incidents.filter((i) => i.status === "Awaiting review").length,
      blocked: incidents.filter((i) => i.action === "Block").length,
      redacted: incidents.filter((i) => i.action === "Redact").length,
      escalated: incidents.filter((i) => i.status === "Escalated" || i.action === "Escalate").length,
      resolved: incidents.filter((i) => i.status === "Resolved").length,
      bySeverity: by((i) => i.severity),
      byCategory: by((i) => i.riskFamily),
      byChannel: by((i) => i.channel),
      byPolicy: by((i) => i.policy),
      byAction: by((i) => i.action),
    };
  }, [incidents]);

  const value = {
    incidents,
    stats,
    currentIncidentId,
    setCurrentIncidentId,
    getIncident,
    runScenario,
    addFromCall,
    reviewIncident,
    markResolved,
    resetDemo,
    buildTimeline,
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be used within DemoProvider");
  return ctx;
}
